/**
 * Error Classifier Utility
 *
 * Classifies job error strings as "system" (infrastructure/environment)
 * or "file" (data/input quality) errors.
 *
 * System errors indicate problems operators should act on immediately
 * (SMB down, network issues, disk full, permissions, config errors).
 *
 * File errors are per-file data quality issues (corrupt ZIP, bad format).
 */

export type ErrorCategory = 'system' | 'file';

/** Patterns that indicate a system/infrastructure error */
const SYSTEM_ERROR_PATTERNS = [
  /smb connection/i,
  /connection timed out/i,
  /connection refused/i,
  /connection reset/i,
  /unable to connect/i,
  /network unreachable/i,
  /no route to host/i,
  /permission\s*error/i,
  /access denied/i,
  /status_access_denied/i,
  /no space left/i,
  /disk quota/i,
  /disk full/i,
  /storage space/i,
  /configuration not found/i,
  /missing parameter/i,
  /invalid configuration/i,
  /configuration error/i,
  /authentication failed/i,
  /service unavailable/i,
  /throttl/i,
  /rate exceeded/i,
  /share not found/i,
];

/** Patterns that indicate a file/data quality error */
const FILE_ERROR_PATTERNS = [
  /badzipfile/i,
  /bad zip/i,
  /invalid zip/i,
  /corrupt/i,
  /bad crc/i,
  /malformed/i,
  /file is damaged/i,
];

/**
 * Classify an error string as system or file error.
 * System errors are checked first. If no pattern matches, defaults to "file".
 */
export function classifyError(error: string): ErrorCategory {
  for (const pattern of SYSTEM_ERROR_PATTERNS) {
    if (pattern.test(error)) return 'system';
  }
  for (const pattern of FILE_ERROR_PATTERNS) {
    if (pattern.test(error)) return 'file';
  }
  // Unknown errors default to system — better to surface them than hide them
  return 'system';
}

/**
 * Get a short, operator-friendly label for a system error.
 */
export function getSystemErrorLabel(error: string): string {
  const lower = error.toLowerCase();
  if (/smb connection|connection timed out|connection refused|connection reset|unable to connect/i.test(lower))
    return 'Print server connection failure';
  if (/network unreachable|no route to host/i.test(lower))
    return 'Network connectivity issue';
  if (/permission|access denied|status_access_denied|authentication failed/i.test(lower))
    return 'Permission / authentication error';
  if (/no space left|disk quota|disk full|storage space/i.test(lower))
    return 'Storage space full';
  if (/configuration|missing parameter/i.test(lower))
    return 'Configuration error';
  if (/service unavailable|throttl|rate exceeded/i.test(lower))
    return 'Service availability issue';
  if (/share not found/i.test(lower))
    return 'SMB share not found';
  return 'System error';
}
